import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Search } from 'lucide-react'
import Button from '@/components/ui/Button'
import Spinner from '@/components/ui/Spinner'
import { toast } from '@/components/ui/Toast'
import {
  findFacilitiesWithinBudget,
  formatManWon,
  type BudgetFacilityResponse,
} from '@/features/carecalc/api'
import type { CareCalcReductionType } from '@/features/carecalc/reduction'

interface Props {
  /** 추정 밴드 */
  band: string
  /** 월 총액 상한(원) — 계산된 시설 비용의 상한을 그대로 쓴다 */
  budgetMax: number
  /**
   * 감경 유형. 예산(budgetMax)이 감경을 반영한 금액이므로 시설도 같은 요율로 걸러야 한다.
   * 빠지면 서버가 일반(20%)으로 계산해 갈 수 있는 시설이 빠진다 (carecalc §3-4-1)
   */
  reductionType: CareCalcReductionType
  /** 프로필에서 설정한 위치. 있으면 GPS 를 다시 묻지 않는다 */
  location?: { latitude: number; longitude: number; addressText: string } | null
}

/**
 * 계산된 예산으로 갈 수 있는 시설.
 *
 * <p>"이 예산으로 갈 수 있는 시설 찾기"라고 말했으면 실제로 그 예산으로 걸러야 한다.
 * 기존 시설 검색은 예산 파라미터를 받아도 배너만 띄우고 필터링하지 않으므로
 * 전용 조회를 쓴다.</p>
 *
 * <p>프로필에서 지역을 고르셨으면 그 지역으로 찾고, 없을 때만 GPS 를 쓴다.
 * GPS 도 거부하시면 일반 시설 검색으로 안내한다.</p>
 */
export default function BudgetFacilityList({ band, budgetMax, reductionType, location }: Props) {
  const navigate = useNavigate()
  const [result, setResult] = useState<BudgetFacilityResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [denied, setDenied] = useState(false)

  const search = async () => {
    // 프로필에서 위치를 주셨으면 권한을 다시 물을 이유가 없다
    if (location) {
      setLoading(true)
      try {
        setResult(await findFacilitiesWithinBudget({
          band,
          budgetMax,
          reductionType,
          lat: location.latitude,
          lng: location.longitude,
        }))
      } catch {
        toast.error('시설을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.')
      } finally {
        setLoading(false)
      }
      return
    }

    if (!navigator.geolocation) {
      setDenied(true)
      return
    }
    setLoading(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const data = await findFacilitiesWithinBudget({
            band,
            budgetMax,
            reductionType,
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          })
          setResult(data)
        } catch {
          toast.error('시설을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.')
        } finally {
          setLoading(false)
        }
      },
      () => {
        setLoading(false)
        setDenied(true)
      },
      { timeout: 8000 },
    )
  }

  // 위치를 못 받으면 일반 검색으로 보낸다
  if (denied) {
    return (
      <div className="rounded-xl bg-cb-gray-50 p-4">
        <p className="text-body-sm leading-relaxed text-cb-gray-700">
          위치를 가져오지 못했어요. 시설 검색에서 직접 지역을 고르실 수 있어요.
        </p>
        <Button
          size="lg"
          fullWidth
          className="mt-3"
          onClick={() => navigate('/facilities')}
        >
          <Search size={18} className="mr-1.5" />
          시설 검색으로 가기
        </Button>
      </div>
    )
  }

  if (!result) {
    return (
      <Button size="lg" fullWidth onClick={search} loading={loading}>
        <MapPin size={18} className="mr-1.5" />
        이 예산으로 갈 수 있는 시설 찾기
      </Button>
    )
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="md" />
      </div>
    )
  }

  // 위치를 주셨으면 어디 기준인지 밝힌다. 조사가 붙는 자리라 두 가지로 나눠 쓴다
  const nearPhrase = location ? `${location.addressText} 주변` : '주변'
  const regionLabel = location ? nearPhrase : '가까운'

  return (
    <div>
      <h2 className="text-h3 font-bold text-cb-gray-900">
        이 예산으로 갈 수 있는 시설 {result.matchedCount}곳
      </h2>
      <p className="mt-1 text-body-sm text-cb-gray-600">
        {regionLabel} 요양원 {result.nearbyCount}곳 중, 월 {formatManWon(result.budgetMax)} 안에
        드는 곳이에요.
      </p>

      {result.facilities.length === 0 ? (
        <div className="mt-3 rounded-xl bg-cb-gray-50 p-4">
          <p className="text-body-sm leading-relaxed text-cb-gray-700">
            {nearPhrase}에 이 예산으로 가실 수 있는 요양원이 없어요. 예산을 조금
            넓히시거나 주야간보호를 함께 알아보시면 선택지가 생깁니다.
          </p>
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            className="mt-3"
            onClick={() => navigate('/facilities')}
          >
            시설 검색에서 조건 바꿔 보기
          </Button>
        </div>
      ) : (
        <>
          <ul className="mt-3 flex flex-col gap-2">
            {result.facilities.map((f) => (
              <li key={f.facilityId}>
                <button
                  type="button"
                  onClick={() => navigate(`/facilities/${f.facilityId}`)}
                  className="w-full rounded-xl border border-cb-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-cb-gray-300"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-body font-bold text-cb-gray-900">{f.name}</span>
                    {f.distanceKm > 0 && (
                      <span className="flex-shrink-0 text-body-sm text-cb-gray-500">
                        {f.distanceKm}km
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-body-sm text-cb-gray-500">{f.address}</p>

                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-body font-bold text-cb-primary text-amount">
                      월 약 {formatManWon(f.totalMonthly)}
                    </span>
                    {f.hasVacancy && (
                      <span className="rounded-md bg-cb-secondary/10 px-2 py-0.5 text-caption font-medium text-cb-secondary">
                        빈자리 있음
                      </span>
                    )}
                    {f.evaluationGrade && (
                      <span className="rounded-md bg-cb-gray-100 px-2 py-0.5 text-caption font-medium text-cb-gray-600">
                        평가 {f.evaluationGrade}등급
                      </span>
                    )}
                  </div>

                  {!f.hasNonCoveredData && (
                    // 비급여 미신고 시설은 총액이 낮게 보인다 — 숨기지 않고 알린다
                    <p className="mt-1 text-caption text-amber-700">
                      식대·상급침실료가 아직 등록되지 않아 실제로는 더 들 수 있어요
                    </p>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <Button
            variant="secondary"
            size="lg"
            fullWidth
            className="mt-3"
            onClick={() => navigate('/facilities')}
          >
            <Search size={18} className="mr-1.5" />
            더 많은 시설 보기
          </Button>
        </>
      )}
    </div>
  )
}
